// components/LocationSelector.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FaMapMarkerAlt, FaChevronDown } from 'react-icons/fa';
import { getRegions, getCommunesByRegion, getRegionByName } from '../../../../../components/data/chileanLocations';

const LocationSelector = ({ 
  selectedRegion, 
  selectedCommune, 
  onRegionChange, 
  onCommuneChange,
  disabled = false,
  showLabels = true,
  regionError = false,
  communeError = false
}) => {
  const [availableCommunes, setAvailableCommunes] = useState([]);
  
  // ✅ Memoizar las regiones para evitar llamadas innecesarias
  const regions = useMemo(() => getRegions(), []);

  // ✅ Memoizar las comunas basadas en la región seleccionada
  const communes = useMemo(() => {
    if (!selectedRegion) return [];
    // Buscar la región por nombre para obtener su ID
    const region = getRegionByName(selectedRegion);
    if (!region) return [];
    return getCommunesByRegion(region.id);
  }, [selectedRegion]);

  // ✅ Actualizar comunas solo cuando cambien las comunas calculadas
  useEffect(() => {
    setAvailableCommunes(communes);
    
    // Si la comuna seleccionada no está en la nueva región, limpiarla
    if (selectedCommune && communes.length > 0 && !communes.find(c => c.name === selectedCommune)) {
      onCommuneChange('');
    }
  }, [communes, selectedCommune, onCommuneChange]);

  // ✅ Limpiar comuna cuando no hay región seleccionada
  useEffect(() => {
    if (!selectedRegion && selectedCommune) {
      onCommuneChange('');
    }
  }, [selectedRegion, selectedCommune, onCommuneChange]);

  const handleRegionChange = useCallback((e) => {
    const selectedRegionName = e.target.value;
    onRegionChange(selectedRegionName); // ✅ Enviar el nombre de la región
  }, [onRegionChange]);

  const handleCommuneChange = useCallback((e) => {
    onCommuneChange(e.target.value);
  }, [onCommuneChange]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Selector de Región */}
      <div className="space-y-2">
        {showLabels && (
          <label className="block text-sm font-medium text-gray-300">
            Región *
          </label>
        )}
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FaMapMarkerAlt className="h-4 w-4 text-gray-400" />
          </div>
          <select
            value={selectedRegion}
            onChange={handleRegionChange}
            disabled={disabled}
            className={`pl-10 pr-10 w-full px-4 py-3 bg-zinc-700/50 border ${
              regionError ? 'border-red-500' : 'border-zinc-600'
            } rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed`}
            required
          >
            <option value="">Selecciona una región</option>
            {regions.map((region) => (
              <option key={region.id} value={region.name} className="bg-zinc-800">
                {region.romanNumber} - {region.name}
              </option>
            ))}
          </select>
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <FaChevronDown className="h-4 w-4 text-gray-400" />
          </div>
        </div>
      </div>

      {/* Selector de Comuna */}
      <div className="space-y-2">
        {showLabels && (
          <label className="block text-sm font-medium text-gray-300">
            Comuna *
          </label>
        )}
        <div className="relative">
          <select
            value={selectedCommune}
            onChange={handleCommuneChange}
            disabled={disabled || !selectedRegion || availableCommunes.length === 0}
            className={`w-full px-4 py-3 bg-zinc-700/50 border ${
              communeError ? 'border-red-500' : 'border-zinc-600'
            } rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed`}
            required
          >
            <option value="">
              {!selectedRegion 
                ? 'Primero selecciona una región' 
                : availableCommunes.length === 0 
                  ? 'Cargando comunas...' 
                  : 'Selecciona una comuna'
              }
            </option>
            {availableCommunes.map((commune) => (
              <option key={commune.id} value={commune.name} className="bg-zinc-800">
                {commune.name}
              </option>
            ))}
          </select>
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <FaChevronDown className="h-4 w-4 text-gray-400" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationSelector;